import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest, postRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const TimeOffPoliciesContext = createContext();

export const TimeOffPoliciesProvider = ({ children }) => {
  const [policies, setPolicies] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchPolicies = useCallback(async () => {
    setPolicies((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(
        `/ahr/time-off-policies${queryString}`
      );
      if (response.success) {
        setPolicies({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setPolicies({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const assignToUser = async (data) => {
    try {
      const response = await postRequest(
        "/ahr/time-off-policies/assign-to-user",
        data
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const assignToAll = async (data) => {
    try {
      const response = await postRequest(
        "/ahr/time-off-policies/assign-to-all-users",
        data
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const totalPages = policies.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const Pagination = () => PaginationComponent;

  return (
    <TimeOffPoliciesContext.Provider
      value={{
        policies,
        filterOptions,
        handleSearch,
        refetch: fetchPolicies,
        assignToUser,
        assignToAll,
        Pagination,
      }}
    >
      {children}
    </TimeOffPoliciesContext.Provider>
  );
};

export const useTimeOffPolicies = () => {
  const context = useContext(TimeOffPoliciesContext);
  if (!context) {
    throw new Error(
      "useTimeOffPolicies must be used within TimeOffPoliciesProvider"
    );
  }

  return context;
};
